import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Boxes, Save, RotateCcw, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { StockBadge } from "@/components/StockBadge";

export default function Stock() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/staff/stock")
    .then(({ data }) => setData(data))
    .catch((e) => toast.error(formatApiError(e, "Could not load stock")));

  useEffect(() => { load(); }, []);

  const warehouses = data ? Object.entries(data.warehouses) : [];
  const items = data ? Object.entries(data.items) : [];

  const startEdit = (id, row) => {
    setEditing(id);
    setNote(row.note || "");
    setDraft(Object.fromEntries(warehouses.map(([wid]) => [wid, row.by_warehouse[wid]?.qty ?? ""])));
  };

  const save = async (id) => {
    setSaving(true);
    try {
      const stock = {};
      for (const [wid, value] of Object.entries(draft)) {
        if (value === "" || value === null) continue;
        stock[wid] = Number(value);
      }
      await api.put(`/staff/stock/${id}`, { stock, note: note || null });
      toast.success("Stock updated");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Could not save stock")); }
    finally { setSaving(false); }
  };

  const reset = async (id) => {
    if (!window.confirm("Drop your count and go back to the supplied stock report for this product?")) return;
    try {
      await api.delete(`/staff/stock/${id}`);
      toast.success("Back to the supplied report");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Could not reset stock")); }
  };

  return (
    <div>
      <Seo title="Stock — EI Battery Workspace" />
      <span className="ei-eyebrow">Inventory</span>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Stock levels</h1>
      <p className="mt-2 text-sm text-ei-graphite/70 max-w-3xl" data-testid="stock-source-note">
        {data ? `${data.source.report}, ${data.source.report_date}. ${data.source.scope}` : "Loading stock source…"}
      </p>
      <p className="mt-2 text-sm text-ei-graphite/70 max-w-3xl">
        Edit a count and it applies everywhere immediately — the stock badge on the site, card payment eligibility,
        the dispatch warehouse and the daily low-stock alert. Counts are reported, never reserved, so always confirm
        before dispatch. Leave a warehouse blank to mean "in stock, count unknown".
      </p>

      <div className="mt-6 bg-white border border-ei-mist rounded-md p-4 sm:p-6 overflow-x-auto" data-testid="stock-table">
        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-ei-navy" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-ei-graphite/60"><Boxes className="w-12 h-12 mx-auto text-ei-mist" /><p className="mt-4 font-semibold text-ei-navy">No stock records</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ei-graphite/55">
                <th className="pb-2 pr-4">Product</th>
                <th className="pb-2 pr-4">Badge</th>
                {warehouses.map(([id, w]) => <th key={id} className="pb-2 pr-4 text-right">{w.code}</th>)}
                <th className="pb-2 pr-4 text-right">Total</th>
                <th className="pb-2 pr-4">Notes</th>
                <th className="pb-2 text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {items.map(([id, row]) => (
                <tr key={id} className="border-t border-ei-mist/70 align-top" data-testid={`stock-row-${id}`}>
                  <td className="py-3 pr-4 font-medium text-ei-navy">{row.name}</td>
                  <td className="py-3 pr-4"><StockBadge slug={id} testId={`stock-badge-${id}`} /></td>
                  {warehouses.map(([wid]) => (
                    <td key={wid} className="py-3 pr-4 text-right tabular-nums" data-testid={`stock-${id}-${wid}`}>
                      {editing === id ? (
                        <input
                          type="number" min="0" inputMode="numeric"
                          value={draft[wid] ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, [wid]: e.target.value }))}
                          className="w-20 rounded-sm border border-ei-mist px-2 py-1.5 text-right"
                          data-testid={`stock-input-${id}-${wid}`}
                        />
                      ) : (
                        row.by_warehouse[wid] ? (row.by_warehouse[wid].qty === null ? "In stock" : row.by_warehouse[wid].qty) : "—"
                      )}
                    </td>
                  ))}
                  <td className="py-3 pr-4 text-right font-semibold tabular-nums" data-testid={`stock-total-${id}`}>
                    {row.total_qty === null ? "—" : row.total_qty}{row.approximate ? " approx." : ""}
                  </td>
                  <td className="py-3 pr-4 text-xs text-ei-graphite/65 max-w-xs">
                    {editing === id ? (
                      <input
                        value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="Note for staff (optional)"
                        className="w-48 rounded-sm border border-ei-mist px-2 py-1.5 text-xs"
                        data-testid={`stock-note-${id}`}
                      />
                    ) : (
                      <>
                        {row.note || (row.public ? "" : "Not published on the public site.")}
                        {row.updated_by && <span className="block mt-1 text-ei-graphite/50">Set by {row.updated_by}</span>}
                      </>
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    {editing === id ? (
                      <span className="inline-flex gap-2">
                        <button onClick={() => save(id)} disabled={saving}
                          className="inline-flex items-center gap-1.5 rounded-sm bg-ei-navy px-3 py-2 text-xs font-bold text-ei-white disabled:opacity-50"
                          data-testid={`stock-save-${id}`}>
                          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                        </button>
                        <button onClick={() => setEditing(null)} className="rounded-sm border border-ei-mist px-3 py-2 text-xs font-semibold text-ei-navy" data-testid={`stock-cancel-${id}`}>Cancel</button>
                      </span>
                    ) : (
                      <span className="inline-flex gap-2">
                        <button onClick={() => startEdit(id, row)}
                          className="inline-flex items-center gap-1.5 rounded-sm border border-ei-navy/25 px-3 py-2 text-xs font-semibold text-ei-navy hover:border-ei-navy"
                          data-testid={`stock-edit-${id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        {data.overridden?.includes(id) && (
                          <button onClick={() => reset(id)} title="Back to the supplied report"
                            className="inline-flex items-center gap-1.5 rounded-sm border border-ei-mist px-3 py-2 text-xs font-semibold text-ei-graphite/80"
                            data-testid={`stock-reset-${id}`}>
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
